import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Search } from "lucide-react";
import Logo from "./Logo";

const NAV = [
  { to: "/reviews", label: "Reviews" },
  { to: "/comparisons", label: "Comparisons" },
  { to: "/guides", label: "Guides" },
  { to: "/categories", label: "Categories" },
  { to: "/about", label: "About" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
  }, [pathname]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearchOpen(false);
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    setQuery("");
  };

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-paper/80 border-b border-neutral-200/70"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Logo />

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-medium link-underline ${
                    isActive ? "text-indigo-800" : "text-neutral-700 hover:text-indigo-800"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Search"
              onClick={() => setSearchOpen((s) => !s)}
              data-testid="header-search-button"
              className="p-2 rounded-md text-neutral-700 hover:text-indigo-800 hover:bg-neutral-100 transition-colors"
            >
              <Search size={18} />
            </button>
            <Link
              to="/reviews"
              data-testid="header-cta"
              className="hidden md:inline-flex items-center whitespace-nowrap text-sm font-medium px-4 py-2 rounded-full bg-indigo-800 text-surface hover:bg-indigo-900 transition-colors"
            >
              Explore Software
            </Link>
            <button
              type="button"
              aria-label="Toggle menu"
              onClick={() => setOpen((s) => !s)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 rounded-md text-neutral-700 hover:bg-neutral-100 transition-colors"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {searchOpen && (
          <form
            onSubmit={onSearchSubmit}
            className="py-3 border-t border-neutral-200/70"
            data-testid="header-search-form"
          >
            <div className="flex items-center gap-2">
              <Search size={16} className="text-neutral-500" />
              <input
                autoFocus
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search reviews, comparisons, guides…"
                data-testid="header-search-input"
                className="flex-1 bg-transparent outline-none text-sm py-2 placeholder:text-neutral-400"
              />
              <button
                type="submit"
                className="text-xs font-semibold uppercase tracking-wider text-indigo-800 hover:text-indigo-950"
                data-testid="header-search-submit"
              >
                Search
              </button>
            </div>
          </form>
        )}

        {open && (
          <div className="lg:hidden pb-5 border-t border-neutral-200/70 pt-3" data-testid="mobile-menu">
            <nav className="flex flex-col gap-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-3 py-2.5 rounded-md text-sm font-medium ${
                      isActive
                        ? "bg-indigo-50 text-indigo-800"
                        : "text-neutral-700 hover:bg-neutral-100"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/reviews"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta"
                className="md:hidden mt-2 px-3 py-2.5 rounded-md text-sm font-semibold bg-indigo-800 text-surface hover:bg-indigo-900 transition-colors"
              >
                Explore Software
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
